import React from 'react';
import { action } from 'mobx';
import { ActiveButton, DefaultButton } from 'ui/public/button';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import baseStyles from './email_verification.module.css';
import EmailVerificationMessages from './email_verification_messages';
import { BasePath } from 'services/base/page_path';
import { useHistory, useLocation } from 'react-router-dom';
import { parseQueryString } from 'services/base/http';
import { StandardPage } from 'ui/base/page/standard_page';
import { useAccountsDomain } from 'services/accounts/accounts_domain';
import { ResultKind } from 'services/base/types';
import { EmailVerificationStore } from './email_verification_store';
import { ErrorMessage } from 'ui/public/error_message/error_message';
import { observer } from 'mobx-react';
import { BackButton } from 'ui/base/button/back_button/back_button';
import { HistoryService } from 'services/base/history';
import { Link } from 'ui/base/link';

type EmailVerificationStyles = {
  header: string;
  reversedHeader: string;
  settingsIcon: string;
  title: string;
  body: string;
  bodyItem: string;
  buttonContainer: string;
};

const themedStyles: ThemedStyles<EmailVerificationStyles> = {
  [Theme.DAYLIGHT]: {
    header: baseStyles.header,
    reversedHeader: baseStyles.reversedHeader,
    settingsIcon: baseStyles.daylightSettingsIcon,
    title: baseStyles.daylightTitle,
    body: baseStyles.daylightBody,
    bodyItem: baseStyles.bodyItem,
    buttonContainer: baseStyles.buttonContainer,
  },
  [Theme.MIDNIGHT]: {
    header: baseStyles.header,
    reversedHeader: baseStyles.reversedHeader,
    settingsIcon: baseStyles.midnightSettingsIcon,
    title: baseStyles.midnightTitle,
    body: baseStyles.midnightBody,
    bodyItem: baseStyles.bodyItem,
    buttonContainer: baseStyles.buttonContainer,
  },
};

type BaseEmailVerificationProps = {
  store: EmailVerificationStore;
};

export const BaseEmailVerification = observer(({ store }: BaseEmailVerificationProps) => {
  const history = useHistory();
  const accounts = useAccountsDomain();
  const location = useLocation();
  const query = parseQueryString(location);
  const email = query.email ?
    query.email :
    EmailVerificationMessages.emailPlaceholder();

  const handleProceed = React.useCallback(action(async() => {
    const result = await store.busyProceed.perform(() => store.handleProceed(accounts));
    if (result === ResultKind.OK) {
      if (accounts.pangolins.size === 0) {
        HistoryService.clear(history);
        HistoryService.replace(history, { kind: BasePath.ADD_PANGOLIN_SETUP });
      } else {
        history.goBack();
      }
    }
  }), [accounts, history, store]);

  const handleResend = React.useCallback(action(async() => {
    const result = await store.busyResend.perform(() => store.handleResend());
    if (result.kind === ResultKind.USER_ERROR) {
      if (result.emailAlreadyVerifiedError) {
        // If the user is already verified, run the code for proceed
        // This will fetch the user, know that it's verified, update the session, then continue
        handleProceed();
      } else if (result.resendThrottleError) {
        // If the email verification service said we're throttled,
        // do nothing
      }
    } else if (result.kind === ResultKind.OK) {
      // Do nothing if the resend was successful
    }
  }), [store, handleProceed]);

  const handleLogout = React.useCallback(async() => {
    const result = await store.busyLogout.perform(() => store.handleLogout(accounts));
    if (result.kind === ResultKind.OK || result.kind === ResultKind.USER_ERROR) {
      HistoryService.clear(history);
      HistoryService.replace(history, { kind: BasePath.INTRO });
    }
  }, [accounts, history, store]);

  const styles = useStyles(themedStyles);

  let header: React.ReactNode;
  if (accounts.user?.emailVerifiedAt == null) {
    // Put a link to edit settings if the user has not verified ever
    header = (
      <div className={styles.reversedHeader}>
        <Link path={{ kind: BasePath.SETTINGS_EDIT }}>
          <div className={styles.settingsIcon}/>
        </Link>
      </div>
    );
  } else {
    header = (
      <div className={styles.header}>
        <BackButton/>
      </div>
    );
  }

  return (
    <StandardPage>
      {header}
      <div className={styles.title}>
        {EmailVerificationMessages.title()}
      </div>
      <div className={styles.body}>
        <div className={styles.bodyItem}>{EmailVerificationMessages.requestedFor(email)}</div>
      </div>
      <ErrorMessage message={store.errorMessage}/>
      <div className={styles.buttonContainer}>
        <ActiveButton busy={store.busyProceed} onClick={handleProceed}>
          {EmailVerificationMessages.proceed()}
        </ActiveButton>
      </div>
      <div className={styles.buttonContainer}>
        <ActiveButton busy={store.busyResend} onClick={handleResend}>
          {EmailVerificationMessages.resend()}
        </ActiveButton>
      </div>
      {accounts.user?.emailVerifiedAt != null ? null :
        <div className={styles.buttonContainer}>
          <DefaultButton busy={store.busyLogout} onClick={handleLogout}>
            {EmailVerificationMessages.logout()}
          </DefaultButton>
        </div>}
    </StandardPage>
  );
});

export const EmailVerification = () => {
  const [store] = React.useState(new EmailVerificationStore());
  return <BaseEmailVerification store={store}/>;
};
